import { useContext, useEffect, useState, useRef } from 'react';
import InputField from 'components/common/input-field';
import Select from 'components/common/select';
import DarkButton from 'components/common/buttons/dark-btn';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import { initSideModal, emailRegEx, usersRoles, scrollUp } from 'helpers/utils';
import { useAppSelector, useAppDispatch } from 'hooks/useAppDispatchSelector';
import { setSideModalData } from 'store/features/modelSlice';
import {
  IRoles,
  addSuperAdminUser,
  getAdminRoleDetails,
  getOrganizationListWithAssociateParking,
  IOrganizationAssociateParking,
  IAdminRoleParkingAddress,
  IAdminstratorRole,
  IRoleDetails,
  editAdminUser,
  addFleetManagerAdminUser,
  IAddEditAdminRoleRequest,
  IRoleDataRequestForFleet,
  IRoleDataRequest,
  editFleetManagerAdminUser,
} from 'store/features/administratorRoleSlice';
import CheckBox from 'components/common/checkbox';
import { ReactComponent as EditIcon } from 'assets/icon/pencil.svg';
import { ReactComponent as DeleteIcon } from 'assets/icon/trash.svg';
import toast from 'react-hot-toast';
import { AppContext } from 'context/app-context';
import CommonLayout from './common-layout';
import { ICompany, getCompanyList } from 'store/features/companySlice';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface IError {
  name: string;
  surname: string;
  email: string;
  organizations: string;
  mobile_no: string;
  birth_date: string;
  address: string;
  role: string;
}

const initAdministratorRole: IAdminstratorRole = {
  name: '',
  surname: '',
  email: '',
  roleDetails: [],
  role: null,
  associated_company: '',
};

const AddEditAdministratorRoles = () => {
  const dispatch = useAppDispatch();
  const { setLoader } = useContext(AppContext);
  const { organizationAssociateParking, adminRoleDetails } = useAppSelector(
    state => state.adminRole,
  );
  const roles = useSelector((state: RootState) => state.adminRole.roles);

  const { selectedOrganization } = useAppSelector(state => state.auth);
  const { companies } = useAppSelector(state => state.company);
  const { sideModal } = useAppSelector(state => state.modal);
  const {
    content: { formFor, callBackFun },
  } = sideModal;
  const [adminRoles, setAdminRoles] = useState<IAdminstratorRole>({ ...initAdministratorRole });
  const [addEditRoleOrg, setAddEditRoleOrg] = useState<IRoleDetails | null>(null);
  const [error, setError] = useState<Partial<IError>>({});
  const [dropdownSearch, serDropdownSearch] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const getParkingDetails = async () => {
    try {
      if (selectedOrganization?.role_name === usersRoles?.DEALER_ADMIN) {
        await dispatch(
          getOrganizationListWithAssociateParking({
            params: { organization_id: selectedOrganization?.organization_id || '' },
          }),
        ).catch(() => {
          setLoader(false);
        });
      } else {
        await dispatch(getOrganizationListWithAssociateParking({ params: {} })).catch(() => {
          setLoader(false);
        });
      }
      return Promise.resolve(true);
    } catch (error) {
      return Promise.reject(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoader(true);
        await getParkingDetails();
        await dispatch(getCompanyList({ params: {}, url: '' }));
        if (formFor === 'EDIT' && adminRoleDetails?.details && adminRoleDetails?.details.length) {
          const userID = adminRoleDetails?.details[0]?.user_details?.id || '';
          await dispatch(getAdminRoleDetails({ user_id: userID }))
            .then(response => {
              setAdminRoles(response);
            })
            .catch(e => {
              setLoader(false);
            });
        }
        setLoader(false);
      } catch (error) {
        setLoader(false);
      }
    };
    fetchData();
  }, []);
  const getErrorData = () => {
    return {
      name: !adminRoles?.name?.trim() ? t('administrator_roles.addEdit.errorNameMessage') : '',
      surname: !adminRoles?.surname?.trim() ? t('administrator_roles.addEdit.errorSurname') : '',
      email: !adminRoles?.email?.trim()
        ? t('administrator_roles.addEdit.errorEmail')
        : !emailRegEx.test(adminRoles?.email)
        ? t('administrator_roles.addEdit.errorValidEmail')
        : '',
    };
  };
  const isValid = () => {
    if (
      !adminRoles.name?.trim() ||
      !adminRoles?.surname?.trim() ||
      !adminRoles?.email?.trim() ||
      !emailRegEx.test(adminRoles?.email)
    ) {
      const output = getErrorData();
      setError(output);
      scrollUp(scrollRef);
      return false;
    } else {
      const output = getErrorData();
      setError(output);
      return true;
    }
  };
  const getRequestBodyData = () => {
    let rolesData: IRoleDataRequest[] | IRoleDataRequestForFleet = adminRoles?.roleDetails
      ?.filter(data => data?.role?.name !== usersRoles?.FLEET_MANAGE && data?.organization?.id)
      .map(roleData => {
        let output: IRoleDataRequest = {
          role: adminRoles?.role?.name || '',
          organization_id: roleData?.organization?.id || '',
          parking_ids:
            roleData?.organization?.parking && roleData?.organization?.parking.length > 0
              ? roleData?.organization?.parking
                  ?.filter(data => data?.isSelected)
                  .map(parkingAdd => parkingAdd?._id || '')
              : [],
        };
        if (adminRoles?.associated_company) {
          output = { ...output, company_id: adminRoles?.associated_company || '' };
        }
        return output;
      });
    let roleDataForFleet: IRoleDataRequestForFleet = {
      role: adminRoles?.role?.name || '',
      company_id: adminRoles?.associated_company || '',
    };
    if (selectedOrganization?.role_name !== usersRoles.SUPER_ADMIN) {
      roleDataForFleet = {
        ...roleDataForFleet,
        organization_id: selectedOrganization?.organization_id,
      };
    }
    if (formFor === 'EDIT') {
      roleDataForFleet = { ...roleDataForFleet, user_role_id: adminRoles?.user_role_id || '' };
    }
    rolesData = adminRoles?.role?.name === usersRoles?.FLEET_MANAGE ? roleDataForFleet : rolesData;
    let request: IAddEditAdminRoleRequest = {
      name: adminRoles?.name || '',
      surname: adminRoles?.surname || '',
      email: adminRoles?.email || '',
      role_details: rolesData,
    };
    if (formFor === 'EDIT') {
      request = { ...request, user_id: adminRoles?.id || '' };
    }
    return request;
  };
  const addAdministratorRole = () => {
    setLoader(true);
    const request = getRequestBodyData();
    dispatch(addSuperAdminUser({ request: request }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('administrator_roles.addEdit.successAddToastMessage'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const editAdministratorRoles = () => {
    setLoader(true);
    const request = getRequestBodyData();
    dispatch(editAdminUser({ request: request }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('administrator_roles.addEdit.successUpdateToastMessage'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const editFleetAdministratorRole = () => {
    setLoader(true);
    const request = getRequestBodyData();
    dispatch(
      editFleetManagerAdminUser({
        user_id: adminRoles?.id || adminRoles?._id || '',
        request: request,
      }),
    )
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('administrator_roles.addEdit.successUpdateToastMessage'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const addFleetAdminstartorRole = () => {
    setLoader(true);
    const request = getRequestBodyData();
    dispatch(addFleetManagerAdminUser({ request: request }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('administrator_roles.addEdit.successAddToastMessage'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleSubmit = () => {
    if (!isValid()) {
      return;
    }
    if (adminRoles.role?.name !== usersRoles.FLEET_MANAGE && !adminRoles?.roleDetails.length) {
      toast.error(t('administrator_roles.addEdit.errorOrganizationDetailToastMessage'));
      return;
    }
    if (
      adminRoles?.role?.name === usersRoles?.FLEET_MANAGE &&
      !adminRoles?.associated_company?.trim()
    ) {
      toast.error(t('administrator_roles.addEdit.errorAssociateCompany'));
      return;
    }
    if (adminRoles?.role?.name === usersRoles?.FLEET_MANAGE) {
      formFor === 'EDIT' ? editFleetAdministratorRole() : addFleetAdminstartorRole();
    } else {
      formFor === 'EDIT' ? editAdministratorRoles() : addAdministratorRole();
    }
  };
  const handleChange = (field: string, value: string | IOrganizationAssociateParking | IRoles) => {
    let updatedData = { ...adminRoles };
    updatedData = { ...updatedData, [field]: value };
    if (field === 'role') {
      updatedData = { ...updatedData, associated_company: '' };
    }
    setAdminRoles(updatedData);
  };

  const handleSelectParkingAddress = (selectedParkingId: string) => {
    const updatedOrganizationParking: IAdminRoleParkingAddress[] =
      addEditRoleOrg?.organization?.parking.map(add => {
        return {
          _id: add?._id,
          address1: add?.address1 || '',
          address2: add?.address2 || '',
          city: add?.city || '',
          state: add?.state || '',
          country: add?.country || '',
          zipcode: add?.zipcode || '',
          is_deleted: add?.is_deleted || false,
          address_title: add?.address_title || '',
          isSelected:
            add?._id === selectedParkingId
              ? add?.isSelected
                ? false
                : true
              : add?.isSelected
              ? true
              : false,
        };
      }) || [];
    setAddEditRoleOrg({
      ...addEditRoleOrg,
      role: addEditRoleOrg?.role ? addEditRoleOrg?.role : null,
      organization: {
        _id: addEditRoleOrg?.organization?._id || '',
        id: addEditRoleOrg?.organization?._id || '',
        name: addEditRoleOrg?.organization?.name || '',
        organization_name: addEditRoleOrg?.organization?.organization_name || '',
        parking: updatedOrganizationParking || [],
      },
    });
  };
  const handleChangeOrganizationDetails = (val: IOrganizationAssociateParking) => {
    setAddEditRoleOrg({
      ...addEditRoleOrg,
      organization: val,
      role: null,
    });
  };

  const handleAddEditOragnizationAndRole = () => {
    if (!addEditRoleOrg?.organization) {
      toast.error(t('administrator_roles.addEdit.errorOrganizationToastMessage'));
      return;
    }
    if (!addEditRoleOrg?.organization?.parking?.find(data => data?.isSelected)) {
      toast.error(t('administrator_roles.addEdit.errorParkingAddressToastMessage'));
      return;
    }
    if (addEditRoleOrg?.isEdited) {
      const updatedRoleDetails = adminRoles?.roleDetails?.map(item =>
        item?.organization?._id === addEditRoleOrg?.organization?._id
          ? { ...addEditRoleOrg }
          : { ...item },
      );
      setAdminRoles({
        ...adminRoles,
        roleDetails: updatedRoleDetails && updatedRoleDetails.length ? [...updatedRoleDetails] : [],
      });
    } else {
      setAdminRoles({
        ...adminRoles,
        roleDetails:
          adminRoles?.roleDetails?.length && addEditRoleOrg
            ? [...adminRoles?.roleDetails, addEditRoleOrg] || []
            : addEditRoleOrg
            ? [addEditRoleOrg]
            : [],
      });
    }
    setAddEditRoleOrg(null);
  };
  const handleDeleteAddedOrganization = (data: IRoleDetails) => {
    const upadtedRoleDetails = adminRoles?.roleDetails?.filter(
      item => item?.organization?.id !== data?.organization?.id,
    );
    setAdminRoles({
      ...adminRoles,
      roleDetails:
        upadtedRoleDetails && upadtedRoleDetails?.length > 0 ? [...upadtedRoleDetails] : [],
    });
  };
  const handleEditOrganization = (roleDetail: IRoleDetails) => {
    const updatedOrganizationParking = organizationAssociateParking
      ?.find(parking => parking?._id === roleDetail?.organization?._id)
      ?.parking.filter(park => !park?.is_deleted)
      .map(data =>
        data?._id &&
        roleDetail?.organization?.parking
          ?.filter(rolePark => !rolePark?.is_deleted && rolePark?.isSelected)
          .find(item => item?._id === data?._id)
          ? { ...data, isSelected: true }
          : { ...data },
      );
    setAddEditRoleOrg({
      ...roleDetail,
      organization: roleDetail.organization
        ? {
            ...roleDetail.organization,
            parking: updatedOrganizationParking?.length ? [...updatedOrganizationParking] : [],
          }
        : null,
      isEdited: true,
    });
  };
  const handleSelectAll = () => {
    const updatedParking = addEditRoleOrg?.organization?.parking.map(data => ({
      ...data,
      isSelected:
        (addEditRoleOrg?.organization?.parking || [])?.filter(data => data?.isSelected).length ===
        (addEditRoleOrg?.organization?.parking || [])?.length
          ? false
          : true,
    }));
    setAddEditRoleOrg(
      addEditRoleOrg
        ? {
            ...addEditRoleOrg,
            organization: addEditRoleOrg?.organization
              ? {
                  ...addEditRoleOrg?.organization,
                  parking: updatedParking?.length ? updatedParking : [],
                }
              : null,
          }
        : null,
    );
  };
  const getOrganizationList = () => {
    return organizationAssociateParking
      .filter(org => org?.organization_name?.toLowerCase().startsWith(dropdownSearch.toLowerCase()))
      .filter(
        ({ _id: id1 }) =>
          !(adminRoles?.roleDetails || []).some(({ organization }) => organization?._id === id1),
      );
  };
  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{
        label:
          formFor === 'EDIT'
            ? t('administrator_roles.addEdit.saveChanges')
            : t('administrator_roles.addEdit.addUser'),
      }}
    >
      <div className="flex flex-col w-full h-full p-6">
        {/*Admin Role Inputs*/}
        <div className="flex flex-wrap -mx-3">
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('administrator_roles.tableHeaders.name')}
              </label>
              <InputField
                placeholder={t('administrator_roles.tableHeaders.name')}
                value={adminRoles?.name}
                onChange={val => handleChange('name', val)}
                error={error?.name || ''}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('administrator_roles.tableHeaders.last_name')}
              </label>
              <InputField
                placeholder={t('administrator_roles.tableHeaders.last_name')}
                value={adminRoles?.surname}
                onChange={val => handleChange('surname', val)}
                error={error?.surname || ''}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('administrator_roles.tableHeaders.email')}
              </label>
              <InputField
                placeholder={t('administrator_roles.tableHeaders.emailPlaceholder')}
                value={adminRoles?.email}
                onChange={val => handleChange('email', val)}
                error={error?.email || ''}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('administrator_roles.addEdit.assignRole')}
              </label>
              <Select
                options={roles}
                selected={adminRoles?.role || null}
                placeholder={t('administrator_roles.addEdit.assignRolePlaceholder')}
                setSelected={data => handleChange('role', data)}
                error={error?.role}
                dropdownPlacement="TOP"
                showCross
                handleCross={() => {
                  setAdminRoles({ ...adminRoles, role: null });
                }}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            {adminRoles?.role?.name === usersRoles?.FLEET_MANAGE && (
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('administrator_roles.addEdit.associateCompany')}
                </label>
                <Select<ICompany>
                  options={companies}
                  selected={
                    companies?.find(
                      data =>
                        data?._id === adminRoles?.associated_company || adminRoles?.company_id,
                    ) || null
                  }
                  placeholder={t('administrator_roles.addEdit.associateCompanyPlaceholder')}
                  setSelected={data => handleChange('associated_company', data?._id || '')}
                  error={error?.role}
                  dropdownPlacement="TOP"
                />
              </div>
            )}
          </div>

          {adminRoles?.role?.name && adminRoles?.role?.name !== usersRoles?.FLEET_MANAGE && (
            <>
              <div className="w-full px-3">
                <label className="font-bold text-base text-black2">
                  {t('administrator_roles.addEdit.organizationDetail')}
                </label>
              </div>
              <div className="w-1/2 px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black w-full">
                    {t('administrator_roles.addEdit.organization')}
                  </label>
                  <Select
                    options={getOrganizationList()}
                    selected={addEditRoleOrg?.organization || null}
                    placeholder={t('administrator_roles.addEdit.organizationPlaceholder')}
                    setSelected={data => handleChangeOrganizationDetails(data)}
                    disabled={addEditRoleOrg?.isEdited}
                    error={error?.organizations}
                    isSearchable
                    search={dropdownSearch}
                    setSearch={val => serDropdownSearch(val)}
                    removeSearchOnBlur={true}
                    showCross
                    handleCross={() => {
                      setAddEditRoleOrg({
                        ...addEditRoleOrg,
                        organization: null,
                        role: null,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="w-1/2 px-3"></div>
              <div className="w-full px-3">
                <div className="flex flex-wrap -mx-3">
                  <div className="w-1/2 px-3">
                    {(addEditRoleOrg?.organization?.parking.filter(add => !add.is_deleted) || [])
                      .length > 0 && (
                      <div className="flex gap-2 relative ps-6">
                        <div className="absolute left-0 top-[-3px] mt-[6px]">
                          <CheckBox
                            checked={
                              (addEditRoleOrg?.organization?.parking || [])?.filter(
                                data => data?.isSelected,
                              ).length === (addEditRoleOrg?.organization?.parking || []).length
                                ? true
                                : false
                            }
                            onChange={() => handleSelectAll()}
                          />
                        </div>
                        <label className="text-base font-medium text-black2">
                          {' '}
                          {t('administrator_roles.addEdit.selectAll')}
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="w-1/2 px-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <PrimaryDarkButton
                        label={`${
                          addEditRoleOrg?.isEdited
                            ? t('administrator_roles.addEdit.saveChanges')
                            : t('administrator_roles.addEdit.add')
                        }`}
                        onClick={() => handleAddEditOragnizationAndRole()}
                        height="h-7"
                        text="text-sm"
                        width="w-1/3"
                      />
                      {addEditRoleOrg && (
                        <DarkButton
                          label={t('CANCEL')}
                          onClick={() => setAddEditRoleOrg(null)}
                          height="h-7"
                          text="text-sm"
                          width="w-1/3"
                        />
                      )}
                    </div>
                  </div>

                  {(addEditRoleOrg?.organization?.parking.filter(add => !add.is_deleted) || [])
                    .length > 0 && (
                    <div className="w-full px-3 mt-2">
                      <div className="flex flex-wrap -mx-3">
                        {(
                          addEditRoleOrg?.organization?.parking.filter(add => !add.is_deleted) || []
                        ).map((parking_add, index) => (
                          <div className="w-1/3 px-3" key={parking_add?._id}>
                            <div className="relative ps-6" key={parking_add?._id}>
                              <div className="absolute left-0 top-[-3px] mt-[6px]">
                                <CheckBox
                                  checked={parking_add?.isSelected}
                                  onChange={() =>
                                    handleSelectParkingAddress(parking_add?._id || '')
                                  }
                                />
                              </div>
                              <label className="block text-base font-bold w-full">
                                {parking_add?.address_title ||
                                  t('administrator_roles.address', { index: index + 1 })}
                              </label>
                              <label className="block text-sm text-black2 w-full break-words">
                                {`${parking_add?.address1}, ${
                                  parking_add?.address2 ? `${parking_add?.address2},` : ''
                                } ${parking_add?.city}, ${parking_add?.state}, ${parking_add?.country} - ${parking_add?.zipcode}.`}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="w-full px-3 max-h-60 overflow-hidden">
                    <div className="block rounded-2 mt-3 bg-bg-50 p-4 mb-3 border-1.5 h-full overflow-y-auto">
                      <div className="flex flex-wrap -mx-3">
                        {(adminRoles?.roleDetails || []).length > 0 ? (
                          (adminRoles?.roleDetails || []).map(roleDetail => (
                            <div className="w-full px-3 mb-3">
                              <div className="flex w-full flex-wrap justify-between">
                                <label className="text-base text-black2 font-bold flex-1">
                                  {roleDetail?.organization?.organization_name}
                                </label>
                                <div className="flex items-center gap-x-1">
                                  <EditIcon
                                    className="cursor-pointer"
                                    height={16}
                                    width={16}
                                    onClick={() => handleEditOrganization(roleDetail)}
                                  />
                                  <DeleteIcon
                                    className="cursor-pointer"
                                    height={16}
                                    width={16}
                                    onClick={() => handleDeleteAddedOrganization(roleDetail)}
                                  />
                                </div>
                              </div>
                              <div className="flex flex-wrap w-full">
                                {roleDetail?.organization?.parking &&
                                  [...roleDetail?.organization?.parking]
                                    .filter(
                                      parkingAdd =>
                                        !parkingAdd?.is_deleted && parkingAdd?.isSelected,
                                    )
                                    .map((parkingAdd, i) => (
                                      <div className="w-1/3 px-2 py-1" key={parkingAdd?._id}>
                                        <label className="w-full block text-base font-bold">
                                          {parkingAdd?.address_title ||
                                            t('administrator_roles.address', { index: i + 1 })}
                                        </label>
                                        <div className="w-full" key={parkingAdd._id}>
                                          <label className="text-sm text-black2 w-full break-words">
                                            {`${parkingAdd?.address1}, ${
                                              parkingAdd?.address2 ? `${parkingAdd?.address2},` : ''
                                            } ${parkingAdd?.city}, ${parkingAdd?.state}, ${parkingAdd?.country} - ${parkingAdd?.zipcode}.`}
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="w-full px-3">
                            <label className="block text-base font-medium text-grey3 text-center w-full">
                              {t('administrator_roles.addEdit.noOrganization')}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddEditAdministratorRoles;
